export const SCHEDULE_STORAGE_VERSION = 1;
export const SCHEDULE_CONVENIENCE_CONTRACT_VERSION = 1;
export const UNGROUPED_PROJECT_GROUP_ID = "__ungrouped__";
export const DEFAULT_PROJECT_GROUP_COLOR = "#64748b";
export const UNGROUPED_PROJECT_GROUP_COLOR = "#94a3b8";

export type ProjectGroup = {
    id: string;
    name: string;
    color: string;
    projectIds: string[];
    collapsed: boolean;
    order: number;
};

export type SavedScheduleView = {
    id: string;
    name: string;
    viewPrefs: any;
    createdAt: string;
    updatedAt: string;
    presetKey?: string;
    source?: string;
    pinned?: boolean;
};

export const ROW_HEIGHT_PRESETS = Object.freeze({
    compact: Object.freeze({ key: "compact", px: 48 }),
    default: Object.freeze({ key: "default", px: 72 }),
    spacious: Object.freeze({ key: "spacious", px: 104 })
});
export const ROW_HEIGHT_MIN_PX = 48;
export const ROW_HEIGHT_MAX_PX = 180;
export const ROW_HEIGHT_DEFAULT_PX = ROW_HEIGHT_PRESETS.default.px;
export const DATE_COLUMN_WIDTH_MIN_PX = 96;
export const DATE_COLUMN_WIDTH_MAX_PX = 320;
export const DATE_COLUMN_WIDTH_DEFAULT_PX = 156;
export const DATE_COLUMN_WIDTH_STEP_PX = 8;
export const PROJECT_ROW_HEIGHT_MIN_PX = 48;
export const PROJECT_ROW_HEIGHT_MAX_PX = 180;
export const PROJECT_ROW_HEIGHT_DEFAULT_PX = 72;
export const PROJECT_ROW_HEIGHT_STEP_PX = 4;
export const PROJECT_ROW_HEADER_WIDTH_PX = 220;
export const NOTE_COLUMN_WIDTH_MIN_PX = 140;
export const NOTE_COLUMN_WIDTH_MAX_PX = 320;
export const NOTE_COLUMN_WIDTH_DEFAULT_PX = 190;
export const NOTE_COLUMN_WIDTH_STEP_PX = 12;
export const PROJECT_COLUMN_WIDTH_MIN_PX = 132;
export const PROJECT_COLUMN_WIDTH_MAX_PX = 320;
export const PROJECT_COLUMN_WIDTH_DEFAULT_PX = 190;
export const PROJECT_COLUMN_WIDTH_STEP_PX = 16;
export const GROUP_SUMMARY_COLUMN_WIDTH_PX = 172;
export const DEFAULT_MILESTONE_COLOR = "#7c3aed";
export const SCHEDULE_PLANNER_TIMELINE_MAX_PROJECTS = 12;
export const SCHEDULE_PLANNER_WORKLOAD_MAX_PEOPLE = 10;

export const ROW_HEIGHT_PRESET_OPTIONS = Object.freeze([
    Object.freeze({ key: "compact", label: "컴팩트" }),
    Object.freeze({ key: "default", label: "기본" }),
    Object.freeze({ key: "spacious", label: "넓게" })
]);

export const VIEW_DENSITY_OPTIONS = Object.freeze(["compact", "standard", "comfortable"]);
export const MOBILE_MODE_OPTIONS = Object.freeze(["entriesOnly", "allDays"]);
export const MILESTONE_SCOPE_OPTIONS = Object.freeze(["common", "project"]);
export const MILESTONE_ICON_OPTIONS = Object.freeze(["flag", "diamond", "pin", "star"]);
export const SCHEDULE_QUICK_VIEW_KEYS = Object.freeze(["default", "today", "week", "mine", "delayed"]);
export const SCHEDULE_DATE_SCOPE_OPTIONS = Object.freeze(["", "today", "week"]);
export const SCHEDULE_QUICK_VIEW_PRESETS = Object.freeze([
    Object.freeze({ key: "default", label: "기본", dateScope: "", ownerOnly: false, status: "", hideEmptyRows: false }),
    Object.freeze({ key: "today", label: "오늘", dateScope: "today", ownerOnly: false, status: "", hideEmptyRows: true }),
    Object.freeze({ key: "week", label: "이번 주", dateScope: "week", ownerOnly: false, status: "", hideEmptyRows: true }),
    Object.freeze({ key: "mine", label: "내 담당", dateScope: "", ownerOnly: true, status: "", hideEmptyRows: true }),
    Object.freeze({ key: "delayed", label: "지연", dateScope: "", ownerOnly: false, status: "지연", hideEmptyRows: true })
]);

export function createDefaultViewPrefs() {
    return {
        version: SCHEDULE_CONVENIENCE_CONTRACT_VERSION,
        rowHeightPreset: "default",
        rowHeightPx: ROW_HEIGHT_DEFAULT_PX,
        rowHeightByDate: {},
        dateColumnWidthPx: DATE_COLUMN_WIDTH_DEFAULT_PX,
        noteColumnWidthPx: NOTE_COLUMN_WIDTH_DEFAULT_PX,
        columnWidthsByDate: {},
        rowHeightsByProject: {},
        projectColumnWidths: {},
        collapsedGroupIds: [],
        density: "standard",
        options: {
            showWeekends: true,
            showHolidays: true,
            showEmptyRows: true,
            dimCompletedEntries: false,
            mobileMode: "entriesOnly"
        },
        filters: {
            searchText: "",
            selectedProjectId: "",
            quickView: "default",
            dateScope: "",
            ownerOnly: false,
            status: "",
            type: "",
            groupId: "",
            favoriteOnly: false,
            milestoneOnly: false,
            hideEmptyRows: false
        }
    };
}

export function createDefaultSavedScheduleViews() {
    return [];
}

export function createDefaultMilestones() {
    return [];
}

export function normalizeMilestones(input = [], projects = []) {
    if (!Array.isArray(input)) return createDefaultMilestones();

    const projectIdSet = toSet(collectProjectIds(projects));
    const seen = {};
    return input
        .map((milestone, index) => {
            if (!isPlainObject(milestone)) return null;

            const date = stringOrDefault(milestone.date, "").trim();
            if (!isDateKey(date)) return null;

            const rawProjectId = stringOrDefault(milestone.projectId || milestone.project_id, "").trim();
            const hasProject = rawProjectId && projectIdSet[rawProjectId] === true;
            const scopeSource = stringOrDefault(milestone.scope || milestone.kind, "").trim();
            const wantsProjectScope = scopeSource === "project" || (!scopeSource && rawProjectId.length > 0);
            if (wantsProjectScope && !hasProject) return null;
            const scope = wantsProjectScope ? "project" : "common";
            const projectId = scope === "project" ? rawProjectId : "";
            const rawId = stringOrDefault(milestone.id, "").trim();
            const id = rawId || `milestone-${date.replace(/-/g, "")}-${projectId || "common"}-${index + 1}`;
            if (seen[id]) return null;
            seen[id] = true;

            return {
                id,
                title: stringOrDefault(milestone.title || milestone.name, "").trim().replace(/\s+/g, " ").slice(0, 64) || "마일스톤",
                date,
                scope,
                projectId,
                color: normalizeMilestoneColor(milestone.color),
                icon: pickStringValue(milestone.icon, MILESTONE_ICON_OPTIONS, "flag"),
                note: stringOrDefault(milestone.note || milestone.memo || milestone.description, "").trim().slice(0, 240),
                order: numberOrDefault(milestone.order, index),
                version: numberOrDefault(milestone.version, 0),
                createdAt: stringOrDefault(milestone.createdAt || milestone.created_at, ""),
                updatedAt: stringOrDefault(milestone.updatedAt || milestone.updated_at, "")
            };
        })
        .filter(milestone => !!milestone)
        .sort(compareMilestones);
}

export function normalizeViewPrefs(input = {}) {
    const defaults = createDefaultViewPrefs();
    const source = isPlainObject(input) ? input : {};
    const sourceOptions = isPlainObject(source.options) ? source.options : {};
    const sourceFilters = isPlainObject(source.filters) ? source.filters : {};
    const rowHeightPreset = pickStringKey(source.rowHeightPreset, ROW_HEIGHT_PRESETS, defaults.rowHeightPreset);
    const hasExplicitRowHeightPx = Object.prototype.hasOwnProperty.call(source, "rowHeightPx")
        || Object.prototype.hasOwnProperty.call(source, "rowHeight");
    const rowHeightPx = hasExplicitRowHeightPx
        ? clampRowHeightPx(source.rowHeightPx === undefined ? source.rowHeight : source.rowHeightPx)
        : getRowHeightPresetPx(rowHeightPreset);
    const showEmptyRows = sourceOptions.showEmptyRows === undefined
        ? !boolOrDefault(sourceFilters.hideEmptyRows, defaults.filters.hideEmptyRows)
        : boolOrDefault(sourceOptions.showEmptyRows, defaults.options.showEmptyRows);

    return {
        version: SCHEDULE_CONVENIENCE_CONTRACT_VERSION,
        rowHeightPreset,
        rowHeightPx,
        rowHeightByDate: normalizeRowHeightByDate(source.rowHeightByDate),
        dateColumnWidthPx: clampDateColumnWidthPx(source.dateColumnWidthPx || source.dateColumnWidth || source.dateWidthPx),
        noteColumnWidthPx: clampNoteColumnWidthPx(source.noteColumnWidthPx || source.noteColumnWidth || source.noteWidthPx),
        columnWidthsByDate: normalizeColumnWidthsByDate(source.columnWidthsByDate || source.dateColumnWidths || (source.columnWidths || {}).dates),
        rowHeightsByProject: normalizeRowHeightsByProject(source.rowHeightsByProject || source.projectRowHeights || (source.rowHeights || {}).projects),
        projectColumnWidths: normalizeProjectColumnWidths(source.projectColumnWidths || source.projectWidths || (source.columnWidths || {}).projects),
        collapsedGroupIds: uniqueStrings(source.collapsedGroupIds),
        density: pickStringValue(source.density, VIEW_DENSITY_OPTIONS, defaults.density),
        options: {
            showWeekends: boolOrDefault(sourceOptions.showWeekends, defaults.options.showWeekends),
            showHolidays: boolOrDefault(sourceOptions.showHolidays, defaults.options.showHolidays),
            showEmptyRows,
            dimCompletedEntries: boolOrDefault(sourceOptions.dimCompletedEntries, defaults.options.dimCompletedEntries),
            mobileMode: pickStringValue(sourceOptions.mobileMode, MOBILE_MODE_OPTIONS, defaults.options.mobileMode)
        },
        filters: {
            searchText: stringOrDefault(sourceFilters.searchText, ""),
            selectedProjectId: stringOrDefault(sourceFilters.selectedProjectId, ""),
            quickView: normalizeScheduleQuickViewKey(sourceFilters.quickView || source.quickView || source.presetKey),
            dateScope: pickStringValue(sourceFilters.dateScope, SCHEDULE_DATE_SCOPE_OPTIONS, defaults.filters.dateScope),
            ownerOnly: boolOrDefault(sourceFilters.ownerOnly, defaults.filters.ownerOnly),
            status: stringOrDefault(sourceFilters.status, ""),
            type: stringOrDefault(sourceFilters.type, ""),
            groupId: stringOrDefault(sourceFilters.groupId, ""),
            favoriteOnly: boolOrDefault(sourceFilters.favoriteOnly, defaults.filters.favoriteOnly),
            milestoneOnly: boolOrDefault(sourceFilters.milestoneOnly, defaults.filters.milestoneOnly),
            hideEmptyRows: !showEmptyRows
        }
    };
}

export function normalizeScheduleConvenienceState(data = {}, projects = [], rows = []) {
    const source = isPlainObject(data) ? data : {};
    const sourceViewPrefs = isPlainObject(source.viewPrefs) ? source.viewPrefs : {};
    const hasExplicitCollapsedGroupIds = Object.prototype.hasOwnProperty.call(sourceViewPrefs, "collapsedGroupIds");
    let viewPrefs = normalizeViewPrefs(sourceViewPrefs);

    if (!hasExplicitCollapsedGroupIds) {
        const collapsedAliasIds = collectCollapsedProjectGroupIds(source.projectGroups);
        if (collapsedAliasIds.length > 0) {
            viewPrefs = normalizeViewPrefs({
                ...viewPrefs,
                collapsedGroupIds: collapsedAliasIds
            });
        }
    }
    viewPrefs = normalizeViewPrefs({
        ...viewPrefs,
        columnWidthsByDate: normalizeColumnWidthsByDate(viewPrefs.columnWidthsByDate, rows),
        projectColumnWidths: normalizeProjectColumnWidths(viewPrefs.projectColumnWidths, projects)
    });

    let projectGroups = normalizeProjectGroups(source.projectGroups, projects, viewPrefs);
    viewPrefs = normalizeViewPrefs({
        ...viewPrefs,
        collapsedGroupIds: normalizeCollapsedGroupIds(viewPrefs.collapsedGroupIds, projectGroups, projects),
        rowHeightsByProject: normalizeRowHeightsByProject(viewPrefs.rowHeightsByProject, projects, projectGroups)
    });
    projectGroups = normalizeProjectGroups(projectGroups, projects, viewPrefs);
    const savedViews = normalizeSavedScheduleViews(source.savedViews, projects, projectGroups);

    return { viewPrefs, projectGroups, savedViews };
}

export function createDefaultProjectGroups() {
    return [];
}

export function createScheduleStoragePayload(input = {}) {
    const source = isPlainObject(input) ? input : {};
    const projects = Array.isArray(source.projects) ? source.projects : [];
    const rows = Array.isArray(source.rows) ? source.rows : [];
    const milestones = normalizeMilestones(source.milestones, projects);
    const convenience = normalizeScheduleConvenienceState({
        viewPrefs: source.viewPrefs,
        projectGroups: source.projectGroups,
        savedViews: source.savedViews
    }, projects, rows);

    return {
        version: numberOrDefault(source.version, SCHEDULE_STORAGE_VERSION),
        projects,
        rows,
        milestones,
        viewPrefs: convenience.viewPrefs,
        projectGroups: convenience.projectGroups,
        savedViews: convenience.savedViews
    };
}

export function normalizeSavedScheduleViews(input = [], projects = [], projectGroups = []) {
    if (!Array.isArray(input)) return createDefaultSavedScheduleViews();

    const projectIdSet = toSet(collectProjectIds(projects));
    const validGroupIds = {};
    getProjectGroupsWithUngrouped(projectGroups, projects).forEach(group => {
        if (!isPlainObject(group) || !group.id) return;
        validGroupIds[group.id] = true;
    });

    const seen = {};
    return input
        .map((view, index) => {
            if (!isPlainObject(view)) return null;
            const id = stringOrDefault(view.id, "").trim();
            if (!id || seen[id]) return null;
            seen[id] = true;

            const name = normalizeSavedScheduleViewName(view.name, `저장된 보기 ${index + 1}`);
            const sourcePrefs = isPlainObject(view.viewPrefs)
                ? view.viewPrefs
                : (isPlainObject(view.prefs) ? view.prefs : {});

            const rawPresetKey = stringOrDefault(view.presetKey || view.quickViewKey || "", "").trim();

            return {
                id,
                name,
                viewPrefs: normalizeSavedScheduleViewPrefs(sourcePrefs, projectIdSet, validGroupIds),
                createdAt: stringOrDefault(view.createdAt || view.created_at, ""),
                updatedAt: stringOrDefault(view.updatedAt || view.updated_at, ""),
                presetKey: rawPresetKey ? normalizeScheduleQuickViewKey(rawPresetKey) : "",
                source: pickStringValue(view.source || view.kind, ["custom", "preset", "system"], "custom"),
                pinned: boolOrDefault(view.pinned || view.isPinned, false)
            };
        })
        .filter(view => !!view);
}

export function normalizeSavedScheduleViewPrefs(input = {}, projectIdSet = {}, validGroupIds = {}) {
    const prefs = normalizeViewPrefs(input);
    const selectedProjectId = stringOrDefault(prefs.filters.selectedProjectId, "").trim();
    const groupId = stringOrDefault(prefs.filters.groupId, "").trim();

    return normalizeViewPrefs({
        ...prefs,
        projectColumnWidths: {},
        columnWidthsByDate: {},
        rowHeightsByProject: {},
        collapsedGroupIds: prefs.collapsedGroupIds.filter(groupId => validGroupIds[groupId] === true),
        filters: {
            ...prefs.filters,
            selectedProjectId: selectedProjectId && projectIdSet[selectedProjectId] === true ? selectedProjectId : "",
            groupId: groupId && validGroupIds[groupId] === true ? groupId : ""
        }
    });
}

export function normalizeScheduleQuickViewKey(value = "") {
    return pickStringValue(value, SCHEDULE_QUICK_VIEW_KEYS, "default");
}

export function getScheduleQuickViewPreset(presetKey = "default") {
    const key = normalizeScheduleQuickViewKey(presetKey);
    return SCHEDULE_QUICK_VIEW_PRESETS.find(preset => preset.key === key) || SCHEDULE_QUICK_VIEW_PRESETS[0];
}

export function applyScheduleQuickViewPreset(viewPrefs = {}, presetKey = "default") {
    const currentPrefs = normalizeViewPrefs(viewPrefs);
    const preset = getScheduleQuickViewPreset(presetKey);
    const nextFilters = {
        ...currentPrefs.filters,
        searchText: "",
        selectedProjectId: "",
        quickView: preset.key,
        dateScope: preset.dateScope,
        ownerOnly: preset.ownerOnly === true,
        status: preset.status || "",
        type: "",
        groupId: "",
        favoriteOnly: false,
        milestoneOnly: false
    };

    return normalizeViewPrefs({
        ...currentPrefs,
        options: {
            ...currentPrefs.options,
            showEmptyRows: preset.hideEmptyRows !== true
        },
        filters: nextFilters
    });
}

export function normalizeSavedScheduleViewName(value, fallback = "저장된 보기") {
    const text = stringOrDefault(value, "").trim().replace(/\s+/g, " ");
    return (text || fallback).slice(0, 32);
}

export function getRowHeightPresetPx(presetKey = "default") {
    const key = pickStringKey(presetKey, ROW_HEIGHT_PRESETS, "default");
    return ROW_HEIGHT_PRESETS[key].px;
}

export function getGlobalRowHeightPx(viewPrefs = {}) {
    return normalizeViewPrefs(viewPrefs).rowHeightPx;
}

export function getRowHeightForDate(viewPrefs = {}, dateKey = "") {
    const prefs = normalizeViewPrefs(viewPrefs);
    const key = stringOrDefault(dateKey, "");
    if (Object.prototype.hasOwnProperty.call(prefs.rowHeightByDate, key)) {
        return prefs.rowHeightByDate[key];
    }
    return prefs.rowHeightPx;
}

export function getRowHeightPresetKeyByPx(heightPx = 0) {
    const height = Math.round(numberOrDefault(heightPx, 0));
    const keys = Object.keys(ROW_HEIGHT_PRESETS);
    for (let index = 0; index < keys.length; index++) {
        const key = keys[index];
        if (ROW_HEIGHT_PRESETS[key].px === height) return key;
    }
    return "";
}

export function clampRowHeightPx(heightPx = ROW_HEIGHT_PRESETS.default.px) {
    const numericHeight = Number(heightPx);
    const height = Math.round(Number.isFinite(numericHeight) ? numericHeight : ROW_HEIGHT_PRESETS.default.px);
    return Math.min(ROW_HEIGHT_MAX_PX, Math.max(ROW_HEIGHT_MIN_PX, height));
}

export function clampDateColumnWidthPx(widthPx = DATE_COLUMN_WIDTH_DEFAULT_PX) {
    const numericWidth = Number(widthPx);
    const width = Math.round(Number.isFinite(numericWidth) ? numericWidth : DATE_COLUMN_WIDTH_DEFAULT_PX);
    return Math.min(DATE_COLUMN_WIDTH_MAX_PX, Math.max(DATE_COLUMN_WIDTH_MIN_PX, width));
}

export function getDateHeaderColumnWidthPx(viewPrefs = {}) {
    return normalizeViewPrefs(viewPrefs).dateColumnWidthPx || DATE_COLUMN_WIDTH_DEFAULT_PX;
}

export function setDateHeaderColumnWidth(viewPrefs = {}, widthPx = DATE_COLUMN_WIDTH_DEFAULT_PX) {
    const prefs = normalizeViewPrefs(viewPrefs);
    return normalizeViewPrefs({
        ...prefs,
        dateColumnWidthPx: clampDateColumnWidthPx(widthPx)
    });
}

export function clearDateHeaderColumnWidth(viewPrefs = {}) {
    const prefs = normalizeViewPrefs(viewPrefs);
    return normalizeViewPrefs({
        ...prefs,
        dateColumnWidthPx: DATE_COLUMN_WIDTH_DEFAULT_PX
    });
}

export function clampProjectRowHeightPx(heightPx = PROJECT_ROW_HEIGHT_DEFAULT_PX) {
    const numericHeight = Number(heightPx);
    const height = Math.round(Number.isFinite(numericHeight) ? numericHeight : PROJECT_ROW_HEIGHT_DEFAULT_PX);
    return Math.min(PROJECT_ROW_HEIGHT_MAX_PX, Math.max(PROJECT_ROW_HEIGHT_MIN_PX, height));
}

export function setGlobalRowHeightPreset(viewPrefs = {}, presetKey = "default") {
    const prefs = normalizeViewPrefs(viewPrefs);
    const key = pickStringKey(presetKey, ROW_HEIGHT_PRESETS, "default");
    return normalizeViewPrefs({
        ...prefs,
        rowHeightPreset: key,
        rowHeightPx: getRowHeightPresetPx(key)
    });
}

export function setGlobalRowHeightPx(viewPrefs = {}, heightPx = ROW_HEIGHT_DEFAULT_PX) {
    const prefs = normalizeViewPrefs(viewPrefs);
    const rowHeightPx = clampRowHeightPx(heightPx);
    const matchedPresetKey = getRowHeightPresetKeyByPx(rowHeightPx);
    return normalizeViewPrefs({
        ...prefs,
        rowHeightPreset: matchedPresetKey || prefs.rowHeightPreset,
        rowHeightPx
    });
}

export function clampProjectColumnWidthPx(widthPx = PROJECT_COLUMN_WIDTH_DEFAULT_PX) {
    const numericWidth = Number(widthPx);
    const width = Math.round(Number.isFinite(numericWidth) ? numericWidth : PROJECT_COLUMN_WIDTH_DEFAULT_PX);
    return Math.min(PROJECT_COLUMN_WIDTH_MAX_PX, Math.max(PROJECT_COLUMN_WIDTH_MIN_PX, width));
}

export function clampNoteColumnWidthPx(widthPx = NOTE_COLUMN_WIDTH_DEFAULT_PX) {
    const numericWidth = Number(widthPx);
    const width = Math.round(Number.isFinite(numericWidth) ? numericWidth : NOTE_COLUMN_WIDTH_DEFAULT_PX);
    return Math.min(NOTE_COLUMN_WIDTH_MAX_PX, Math.max(NOTE_COLUMN_WIDTH_MIN_PX, width));
}

export function getNoteColumnWidthPx(viewPrefs = {}) {
    return normalizeViewPrefs(viewPrefs).noteColumnWidthPx || NOTE_COLUMN_WIDTH_DEFAULT_PX;
}

export function setNoteColumnWidth(viewPrefs = {}, widthPx = NOTE_COLUMN_WIDTH_DEFAULT_PX) {
    const prefs = normalizeViewPrefs(viewPrefs);
    return normalizeViewPrefs({
        ...prefs,
        noteColumnWidthPx: clampNoteColumnWidthPx(widthPx)
    });
}

export function clearNoteColumnWidth(viewPrefs = {}) {
    const prefs = normalizeViewPrefs(viewPrefs);
    return normalizeViewPrefs({
        ...prefs,
        noteColumnWidthPx: NOTE_COLUMN_WIDTH_DEFAULT_PX
    });
}

export function normalizeProjectColumnWidths(input = {}, projectsOrSet = null) {
    if (!isPlainObject(input)) return {};

    let hasProjectFilter = false;
    const validProjectIds = {};
    if (Array.isArray(projectsOrSet)) {
        collectProjectIds(projectsOrSet).forEach(projectId => validProjectIds[projectId] = true);
        hasProjectFilter = true;
    } else if (isPlainObject(projectsOrSet)) {
        Object.keys(projectsOrSet).forEach(projectId => {
            if (projectsOrSet[projectId] === true) validProjectIds[projectId] = true;
        });
        hasProjectFilter = true;
    }

    return Object.keys(input).reduce((result, rawProjectId) => {
        const projectId = stringOrDefault(rawProjectId, "").trim();
        if (!projectId || (hasProjectFilter && validProjectIds[projectId] !== true)) return result;
        const width = Number(input[rawProjectId]);
        if (!Number.isFinite(width)) return result;
        result[projectId] = clampProjectColumnWidthPx(width);
        return result;
    }, {});
}

export function getProjectColumnWidthPx(viewPrefs = {}, projectId = "") {
    const prefs = normalizeViewPrefs(viewPrefs);
    const projectKey = stringOrDefault(projectId, "").trim();
    if (!projectKey) return PROJECT_COLUMN_WIDTH_DEFAULT_PX;
    return prefs.projectColumnWidths[projectKey] || PROJECT_COLUMN_WIDTH_DEFAULT_PX;
}

export function setProjectColumnWidth(viewPrefs = {}, projectId = "", widthPx = PROJECT_COLUMN_WIDTH_DEFAULT_PX) {
    const prefs = normalizeViewPrefs(viewPrefs);
    const projectKey = stringOrDefault(projectId, "").trim();
    if (!projectKey) return prefs;
    return normalizeViewPrefs({
        ...prefs,
        projectColumnWidths: {
            ...prefs.projectColumnWidths,
            [projectKey]: clampProjectColumnWidthPx(widthPx)
        }
    });
}

export function clearProjectColumnWidth(viewPrefs = {}, projectId = "") {
    const prefs = normalizeViewPrefs(viewPrefs);
    const projectKey = stringOrDefault(projectId, "").trim();
    if (!projectKey || !Object.prototype.hasOwnProperty.call(prefs.projectColumnWidths, projectKey)) return prefs;
    const projectColumnWidths = { ...prefs.projectColumnWidths };
    delete projectColumnWidths[projectKey];
    return normalizeViewPrefs({
        ...prefs,
        projectColumnWidths
    });
}

export function normalizeColumnWidthsByDate(input = {}, rowsOrSet = null) {
    if (!isPlainObject(input)) return {};

    let hasDateFilter = false;
    const validDates = {};
    if (Array.isArray(rowsOrSet)) {
        collectDateKeys(rowsOrSet).forEach(dateKey => validDates[dateKey] = true);
        hasDateFilter = true;
    } else if (isPlainObject(rowsOrSet)) {
        Object.keys(rowsOrSet).forEach(dateKey => {
            if (rowsOrSet[dateKey] === true && isDateKey(dateKey)) validDates[dateKey] = true;
        });
        hasDateFilter = true;
    }

    return Object.keys(input).reduce((result, rawDateKey) => {
        const dateKey = stringOrDefault(rawDateKey, "").trim();
        if (!isDateKey(dateKey) || (hasDateFilter && validDates[dateKey] !== true)) return result;
        const width = Number(input[rawDateKey]);
        if (!Number.isFinite(width)) return result;
        result[dateKey] = clampDateColumnWidthPx(width);
        return result;
    }, {});
}

export function getDateColumnWidthPx(viewPrefs = {}, dateKey = "") {
    const prefs = normalizeViewPrefs(viewPrefs);
    const key = stringOrDefault(dateKey, "").trim();
    if (!isDateKey(key)) return DATE_COLUMN_WIDTH_DEFAULT_PX;
    return prefs.columnWidthsByDate[key] || DATE_COLUMN_WIDTH_DEFAULT_PX;
}

export function setDateColumnWidth(viewPrefs = {}, dateKey = "", widthPx = DATE_COLUMN_WIDTH_DEFAULT_PX) {
    const prefs = normalizeViewPrefs(viewPrefs);
    const key = stringOrDefault(dateKey, "").trim();
    if (!isDateKey(key)) return prefs;
    return normalizeViewPrefs({
        ...prefs,
        columnWidthsByDate: {
            ...prefs.columnWidthsByDate,
            [key]: clampDateColumnWidthPx(widthPx)
        }
    });
}

export function clearDateColumnWidth(viewPrefs = {}, dateKey = "") {
    const prefs = normalizeViewPrefs(viewPrefs);
    const key = stringOrDefault(dateKey, "").trim();
    if (!isDateKey(key) || !Object.prototype.hasOwnProperty.call(prefs.columnWidthsByDate, key)) return prefs;
    const columnWidthsByDate = { ...prefs.columnWidthsByDate };
    delete columnWidthsByDate[key];
    return normalizeViewPrefs({
        ...prefs,
        columnWidthsByDate
    });
}

export function normalizeRowHeightsByProject(input = {}, projectsOrSet = null, projectGroups = []) {
    if (!isPlainObject(input)) return {};

    let hasRowFilter = false;
    const validRowIds = {};
    if (Array.isArray(projectsOrSet)) {
        collectProjectIds(projectsOrSet).forEach(projectId => validRowIds[projectId] = true);
        normalizeProjectGroups(projectGroups, projectsOrSet).forEach(group => validRowIds[getProjectGroupRowId(group.id)] = true);
        const ungrouped = createUngroupedProjectGroup(projectsOrSet, projectGroups);
        if (ungrouped.projectIds.length > 0) validRowIds[getProjectGroupRowId(UNGROUPED_PROJECT_GROUP_ID)] = true;
        hasRowFilter = true;
    } else if (isPlainObject(projectsOrSet)) {
        Object.keys(projectsOrSet).forEach(rowId => {
            if (projectsOrSet[rowId] === true) validRowIds[normalizeProjectRowHeightKey(rowId)] = true;
        });
        hasRowFilter = true;
    }

    return Object.keys(input).reduce((result, rawRowId) => {
        const rowId = normalizeProjectRowHeightKey(rawRowId);
        if (!rowId || (hasRowFilter && validRowIds[rowId] !== true)) return result;
        const height = Number(input[rawRowId]);
        if (!Number.isFinite(height)) return result;
        result[rowId] = clampProjectRowHeightPx(height);
        return result;
    }, {});
}

export function normalizeProjectRowHeightKey(value = "") {
    const key = stringOrDefault(value, "").trim();
    if (!key) return "";
    if (key.indexOf("group:") === 0) {
        const groupId = key.slice("group:".length).trim();
        return groupId ? getProjectGroupRowId(groupId) : "";
    }
    if (key.indexOf("project:") === 0) {
        return key.slice("project:".length).trim();
    }
    return key;
}

export function getProjectGroupRowId(groupId = "") {
    const id = stringOrDefault(groupId, "").trim();
    return id ? `group:${id}` : "";
}

export function getProjectRowHeightPx(viewPrefs = {}, rowId = "") {
    const prefs = normalizeViewPrefs(viewPrefs);
    const key = normalizeProjectRowHeightKey(rowId);
    if (!key) return PROJECT_ROW_HEIGHT_DEFAULT_PX;
    return prefs.rowHeightsByProject[key] || PROJECT_ROW_HEIGHT_DEFAULT_PX;
}

export function setProjectRowHeight(viewPrefs = {}, rowId = "", heightPx = PROJECT_ROW_HEIGHT_DEFAULT_PX) {
    const prefs = normalizeViewPrefs(viewPrefs);
    const key = normalizeProjectRowHeightKey(rowId);
    if (!key) return prefs;
    return normalizeViewPrefs({
        ...prefs,
        rowHeightsByProject: {
            ...prefs.rowHeightsByProject,
            [key]: clampProjectRowHeightPx(heightPx)
        }
    });
}

export function clearProjectRowHeight(viewPrefs = {}, rowId = "") {
    const prefs = normalizeViewPrefs(viewPrefs);
    const key = normalizeProjectRowHeightKey(rowId);
    if (!key || !Object.prototype.hasOwnProperty.call(prefs.rowHeightsByProject, key)) return prefs;
    const rowHeightsByProject = { ...prefs.rowHeightsByProject };
    delete rowHeightsByProject[key];
    return normalizeViewPrefs({
        ...prefs,
        rowHeightsByProject
    });
}

export function setRowHeightForDate(viewPrefs = {}, dateKey = "", heightPx = 0) {
    const prefs = normalizeViewPrefs(viewPrefs);
    const key = stringOrDefault(dateKey, "");
    if (!isDateKey(key)) return prefs;

    const height = clampRowHeightPx(heightPx);

    return normalizeViewPrefs({
        ...prefs,
        rowHeightByDate: {
            ...prefs.rowHeightByDate,
            [key]: height
        }
    });
}

export function clearRowHeightForDate(viewPrefs = {}, dateKey = "") {
    const prefs = normalizeViewPrefs(viewPrefs);
    const key = stringOrDefault(dateKey, "");
    if (!isDateKey(key)) return prefs;

    const rowHeightByDate = { ...prefs.rowHeightByDate };
    delete rowHeightByDate[key];
    return normalizeViewPrefs({
        ...prefs,
        rowHeightByDate
    });
}

export function normalizeProjectGroups(input = [], projects = [], viewPrefs = {}) {
    const projectIds = collectProjectIds(projects);
    const projectIdSet = toSet(projectIds);
    const collapsedIds = toSet(isPlainObject(viewPrefs) ? viewPrefs.collapsedGroupIds : []);
    const readCollapsedAlias = !isPlainObject(viewPrefs) || !Object.prototype.hasOwnProperty.call(viewPrefs, "collapsedGroupIds");
    const seenGroupIds = {};
    const candidates = [];

    if (Array.isArray(input)) {
        input.forEach((group, index) => {
            if (!isPlainObject(group)) return;
            const id = stringOrDefault(group.id, "").trim();
            if (!id || id === UNGROUPED_PROJECT_GROUP_ID || seenGroupIds[id]) return;
            seenGroupIds[id] = true;

            const candidateProjectIds = uniqueStrings(group.projectIds)
                .filter(projectId => projectIdSet[projectId]);
            if (candidateProjectIds.length === 0) return;

            candidates.push({
                id,
                name: stringOrDefault(group.name, "").trim() || "프로젝트 그룹",
                color: normalizeProjectGroupColor(group.color),
                projectIds: candidateProjectIds,
                collapsed: collapsedIds[id] === true || (readCollapsedAlias && group.collapsed === true),
                sourceOrder: normalizeProjectGroupOrder(group, index),
                sourceIndex: index
            });
        });
    }

    const assigned = {};
    const groups = [];

    candidates
        .sort(compareProjectGroupCandidates)
        .forEach(candidate => {
            const normalizedProjectIds = candidate.projectIds.filter(projectId => {
                if (assigned[projectId]) return false;
                assigned[projectId] = true;
                return true;
            });
            if (normalizedProjectIds.length === 0) return;
            groups.push({
                id: candidate.id,
                name: candidate.name,
                color: candidate.color,
                projectIds: normalizedProjectIds,
                collapsed: candidate.collapsed,
                order: groups.length
            });
        });

    return groups;
}

export function migrateProjectGroups(input = [], projects = [], viewPrefs = {}) {
    return normalizeProjectGroups(input, projects, viewPrefs);
}

export function addProjectGroupToState(projectGroups = [], projects = [], group = {}, viewPrefs = {}) {
    if (!isPlainObject(group)) return normalizeProjectGroups(projectGroups, projects, viewPrefs);

    const id = stringOrDefault(group.id, "").trim();
    if (!id || id === UNGROUPED_PROJECT_GROUP_ID) return normalizeProjectGroups(projectGroups, projects, viewPrefs);

    const projectIdSet = toSet(collectProjectIds(projects));
    const projectIds = uniqueStrings(group.projectIds).filter(projectId => projectIdSet[projectId]);
    if (projectIds.length === 0) return normalizeProjectGroups(projectGroups, projects, viewPrefs);

    const existingGroups = normalizeProjectGroups(projectGroups, projects, viewPrefs)
        .filter(item => item.id !== id)
        .map(item => ({
            ...item,
            projectIds: item.projectIds.filter(projectId => projectIds.indexOf(projectId) < 0)
        }));

    return normalizeProjectGroups(existingGroups.concat([{
        id,
        name: stringOrDefault(group.name, "").trim() || "프로젝트 그룹",
        color: group.color,
        projectIds,
        order: numberOrDefault(group.order, existingGroups.length)
    }]), projects, viewPrefs);
}

export function updateProjectGroupInState(projectGroups = [], projects = [], groupId = "", changes = {}, viewPrefs = {}) {
    const id = stringOrDefault(groupId, "").trim();
    if (!id || id === UNGROUPED_PROJECT_GROUP_ID || !isPlainObject(changes)) {
        return normalizeProjectGroups(projectGroups, projects, viewPrefs);
    }

    const groups = normalizeProjectGroups(projectGroups, projects, viewPrefs);
    return normalizeProjectGroups(groups.map(group => {
        if (group.id !== id) return group;
        return {
            ...group,
            name: changes.name === undefined ? group.name : stringOrDefault(changes.name, "").trim(),
            color: changes.color === undefined ? group.color : changes.color
        };
    }), projects, viewPrefs);
}

export function deleteProjectGroupFromState(projectGroups = [], projects = [], groupId = "", viewPrefs = {}) {
    const id = stringOrDefault(groupId, "").trim();
    const currentPrefs = normalizeViewPrefs(viewPrefs);
    const nextPrefs = normalizeViewPrefs({
        ...currentPrefs,
        collapsedGroupIds: currentPrefs.collapsedGroupIds.filter(item => item !== id)
    });

    if (!id || id === UNGROUPED_PROJECT_GROUP_ID) {
        return {
            projectGroups: normalizeProjectGroups(projectGroups, projects, nextPrefs),
            viewPrefs: normalizeViewPrefs({
                ...nextPrefs,
                collapsedGroupIds: normalizeCollapsedGroupIds(nextPrefs.collapsedGroupIds, projectGroups, projects)
            })
        };
    }

    const groups = normalizeProjectGroups(projectGroups, projects, currentPrefs)
        .filter(group => group.id !== id);
    const normalizedGroups = normalizeProjectGroups(groups, projects, nextPrefs);
    return {
        projectGroups: normalizedGroups,
        viewPrefs: normalizeViewPrefs({
            ...nextPrefs,
            collapsedGroupIds: normalizeCollapsedGroupIds(nextPrefs.collapsedGroupIds, normalizedGroups, projects)
        })
    };
}

export function assignProjectGroupInState(projectGroups = [], projects = [], projectId = "", groupId = "", viewPrefs = {}) {
    const projectKey = stringOrDefault(projectId, "").trim();
    const targetGroupId = stringOrDefault(groupId, "").trim();
    const projectIdSet = toSet(collectProjectIds(projects));
    if (!projectKey || !projectIdSet[projectKey]) return normalizeProjectGroups(projectGroups, projects, viewPrefs);

    const groups = normalizeProjectGroups(projectGroups, projects, viewPrefs)
        .map(group => ({
            ...group,
            projectIds: group.projectIds.filter(id => id !== projectKey)
        }));

    if (targetGroupId && targetGroupId !== UNGROUPED_PROJECT_GROUP_ID) {
        const targetGroup = groups.find(group => group.id === targetGroupId);
        if (targetGroup) targetGroup.projectIds = targetGroup.projectIds.concat([projectKey]);
    }

    return normalizeProjectGroups(groups, projects, viewPrefs);
}

export function moveProjectGroupInState(projectGroups = [], projects = [], groupId = "", direction = "", viewPrefs = {}) {
    const id = stringOrDefault(groupId, "").trim();
    if (!id || id === UNGROUPED_PROJECT_GROUP_ID) return normalizeProjectGroups(projectGroups, projects, viewPrefs);

    const groups = normalizeProjectGroups(projectGroups, projects, viewPrefs);
    const currentIndex = groups.findIndex(group => group.id === id);
    const targetIndex = getMoveTargetIndex(currentIndex, groups.length, direction);
    if (targetIndex === currentIndex) return groups;

    return normalizeProjectGroups(
        swapArrayItems(groups, currentIndex, targetIndex)
            .map((group, index) => ({
                ...group,
                order: index
            })),
        projects,
        viewPrefs
    );
}

export function moveProjectInGroupInState(projectGroups = [], projects = [], groupId = "", projectId = "", direction = "", viewPrefs = {}) {
    const targetGroupId = stringOrDefault(groupId, "").trim();
    const targetProjectId = stringOrDefault(projectId, "").trim();
    if (!targetGroupId || targetGroupId === UNGROUPED_PROJECT_GROUP_ID || !targetProjectId) {
        return normalizeProjectGroups(projectGroups, projects, viewPrefs);
    }

    const groups = normalizeProjectGroups(projectGroups, projects, viewPrefs);
    const groupIndex = groups.findIndex(group => group.id === targetGroupId);
    if (groupIndex < 0) return groups;

    const projectIndex = groups[groupIndex].projectIds.indexOf(targetProjectId);
    const targetIndex = getMoveTargetIndex(projectIndex, groups[groupIndex].projectIds.length, direction);
    if (targetIndex === projectIndex) return groups;

    return normalizeProjectGroups(groups.map(group => {
        if (group.id !== targetGroupId) return group;
        return {
            ...group,
            projectIds: swapArrayItems(group.projectIds, projectIndex, targetIndex)
        };
    }), projects, viewPrefs);
}

export function moveProjectInGroupToIndexInState(projectGroups = [], projects = [], groupId = "", projectId = "", targetIndex = 0, viewPrefs = {}) {
    const targetGroupId = stringOrDefault(groupId, "").trim();
    const targetProjectId = stringOrDefault(projectId, "").trim();
    if (!targetGroupId || targetGroupId === UNGROUPED_PROJECT_GROUP_ID || !targetProjectId) {
        return normalizeProjectGroups(projectGroups, projects, viewPrefs);
    }

    const groups = normalizeProjectGroups(projectGroups, projects, viewPrefs);
    const groupIndex = groups.findIndex(group => group.id === targetGroupId);
    if (groupIndex < 0) return groups;

    const projectIndex = groups[groupIndex].projectIds.indexOf(targetProjectId);
    const nextProjectIds = moveArrayItemToIndex(groups[groupIndex].projectIds, projectIndex, targetIndex);
    if (arraysEqual(nextProjectIds, groups[groupIndex].projectIds)) return groups;

    return normalizeProjectGroups(groups.map(group => {
        if (group.id !== targetGroupId) return group;
        return {
            ...group,
            projectIds: nextProjectIds
        };
    }), projects, viewPrefs);
}

export function moveUngroupedProjectInProjectList(projects = [], projectGroups = [], projectId = "", direction = "") {
    if (!Array.isArray(projects)) return [];

    const projectKey = stringOrDefault(projectId, "").trim();
    const normalizedGroups = normalizeProjectGroups(projectGroups, projects);
    const ungroupedIds = collectUngroupedProjectIds(projects, normalizedGroups);
    const currentIndex = ungroupedIds.indexOf(projectKey);
    const targetIndex = getMoveTargetIndex(currentIndex, ungroupedIds.length, direction);
    if (targetIndex === currentIndex) return normalizeProjectSortOrders(projects);

    const nextUngroupedIds = swapArrayItems(ungroupedIds, currentIndex, targetIndex);
    const projectMap = mapProjectsById(projects);
    const ungroupedSet = toSet(ungroupedIds);
    let ungroupedCursor = 0;

    return normalizeProjectSortOrders(projects.map(project => {
        const id = isPlainObject(project) ? stringOrDefault(project.id, "").trim() : "";
        if (!ungroupedSet[id]) return project;
        const nextId = nextUngroupedIds[ungroupedCursor++];
        return projectMap[nextId] || project;
    }));
}

export function moveUngroupedProjectInProjectListToIndex(projects = [], projectGroups = [], projectId = "", targetIndex = 0) {
    if (!Array.isArray(projects)) return [];

    const projectKey = stringOrDefault(projectId, "").trim();
    const normalizedGroups = normalizeProjectGroups(projectGroups, projects);
    const ungroupedIds = collectUngroupedProjectIds(projects, normalizedGroups);
    const currentIndex = ungroupedIds.indexOf(projectKey);
    const nextUngroupedIds = moveArrayItemToIndex(ungroupedIds, currentIndex, targetIndex);
    if (arraysEqual(nextUngroupedIds, ungroupedIds)) return normalizeProjectSortOrders(projects);

    const projectMap = mapProjectsById(projects);
    const ungroupedSet = toSet(ungroupedIds);
    let ungroupedCursor = 0;

    return normalizeProjectSortOrders(projects.map(project => {
        const id = isPlainObject(project) ? stringOrDefault(project.id, "").trim() : "";
        if (!ungroupedSet[id]) return project;
        const nextId = nextUngroupedIds[ungroupedCursor++];
        return projectMap[nextId] || project;
    }));
}

export function getUngroupedProjectIds(projects = [], projectGroups = []) {
    const normalizedGroups = normalizeProjectGroups(projectGroups, projects);
    return collectUngroupedProjectIds(projects, normalizedGroups);
}

export function createUngroupedProjectGroup(projects = [], projectGroups = [], viewPrefs = {}) {
    const normalizedGroups = normalizeProjectGroups(projectGroups, projects, viewPrefs);
    const collapsedIds = toSet(isPlainObject(viewPrefs) ? viewPrefs.collapsedGroupIds : []);
    return {
        id: UNGROUPED_PROJECT_GROUP_ID,
        name: "미분류",
        color: UNGROUPED_PROJECT_GROUP_COLOR,
        projectIds: collectUngroupedProjectIds(projects, normalizedGroups),
        collapsed: collapsedIds[UNGROUPED_PROJECT_GROUP_ID] === true,
        order: normalizedGroups.length
    };
}

export function getProjectGroupsWithUngrouped(projectGroups = [], projects = [], viewPrefs = {}) {
    const normalizedGroups = normalizeProjectGroups(projectGroups, projects, viewPrefs);
    const ungroupedGroup = createUngroupedProjectGroup(projects, normalizedGroups, viewPrefs);
    if (ungroupedGroup.projectIds.length === 0) return normalizedGroups;
    return normalizedGroups.concat([ungroupedGroup]);
}

export function getProjectGroupRenderItems(projectGroups = [], projects = [], viewPrefs = {}) {
    const prefs = normalizeViewPrefs(viewPrefs);
    const filters = prefs.filters || {};
    const projectMap = mapProjectsById(projects);
    const groups = getProjectGroupsWithUngrouped(projectGroups, projects, prefs);
    const selectedProjectId = stringOrDefault(filters.selectedProjectId, "").trim();
    const groupId = stringOrDefault(filters.groupId, "").trim();
    const favoriteOnly = filters.favoriteOnly === true;
    const hasSelectedProject = !!(selectedProjectId && projectMap[selectedProjectId]);
    const hasGroupFilter = !!groupId && groups.some(group => group && group.id === groupId);

    return groups
        .filter(group => isPlainObject(group))
        .filter(group => !hasGroupFilter || group.id === groupId)
        .map(group => {
            let projectIds = uniqueStrings(group.projectIds)
                .filter(projectId => !!projectMap[projectId] && !isHiddenProject(projectMap[projectId]));
            if (favoriteOnly) projectIds = projectIds.filter(projectId => isFavoriteProject(projectMap[projectId]));
            if (hasSelectedProject) projectIds = projectIds.filter(projectId => projectId === selectedProjectId);
            if (projectIds.length === 0) return null;
            return {
                ...group,
                projectIds,
                collapsed: hasSelectedProject ? false : group.collapsed
            };
        })
        .filter(group => !!group);
}

export function getProjectRenderOrder(projectGroups = [], projects = [], viewPrefs = {}) {
    const projectIds = collectVisibleProjectIds(projects);
    const projectIdSet = toSet(projectIds);
    const groups = getProjectGroupsWithUngrouped(projectGroups, projects, viewPrefs);
    const seen = {};
    const ordered = [];

    groups.forEach(group => {
        if (!isPlainObject(group)) return;
        uniqueStrings(group.projectIds).forEach(projectId => {
            if (!projectIdSet[projectId] || seen[projectId]) return;
            seen[projectId] = true;
            ordered.push(projectId);
        });
    });

    projectIds.forEach(projectId => {
        if (seen[projectId]) return;
        seen[projectId] = true;
        ordered.push(projectId);
    });

    return ordered;
}

export function getProjectColumnRenderItems(projectGroups = [], projects = [], viewPrefs = {}) {
    const projectMap = mapProjectsById(projects);
    const groups = getProjectGroupRenderItems(projectGroups, projects, viewPrefs);
    const seen = {};
    const columns = [];

    groups.forEach(group => {
        if (!isPlainObject(group)) return;
        const projectIds = uniqueStrings(group.projectIds)
            .filter(projectId => !!projectMap[projectId] && !isHiddenProject(projectMap[projectId]));
        if (projectIds.length === 0) return;

        if (group.collapsed === true) {
            projectIds.forEach(projectId => seen[projectId] = true);
            columns.push({
                type: "groupSummary",
                id: `group-summary-${group.id}`,
                groupId: group.id,
                widthPx: GROUP_SUMMARY_COLUMN_WIDTH_PX,
                group: {
                    ...group,
                    projectIds
                }
            });
            return;
        }

        projectIds.forEach(projectId => {
            if (seen[projectId]) return;
            seen[projectId] = true;
            columns.push({
                type: "project",
                id: projectId,
                projectId,
                groupId: group.id,
                widthPx: getProjectColumnWidthPx(viewPrefs, projectId),
                project: projectMap[projectId]
            });
        });
    });

    return columns;
}

export function normalizeRowHeightByDate(input = {}) {
    if (!isPlainObject(input)) return {};
    return Object.keys(input).reduce((result, dateKey) => {
        if (!isDateKey(dateKey)) return result;
        const height = Number(input[dateKey]);
        if (!Number.isFinite(height)) return result;
        result[dateKey] = clampRowHeightPx(height);
        return result;
    }, {});
}

export function createSchedulePlannerFilterContext(input = {}) {
    const source = isPlainObject(input) ? input : {};
    const todayKey = isDateKey(source.todayKey) ? source.todayKey : "";
    const weekStartKey = isDateKey(source.weekStartKey)
        ? source.weekStartKey
        : (todayKey ? getPlannerWeekStartKey(todayKey) : "");
    const nextWeekStartKey = isDateKey(source.nextWeekStartKey)
        ? source.nextWeekStartKey
        : (weekStartKey ? addDaysToDateKey(weekStartKey, 7) : "");

    return {
        keyword: stringOrDefault(source.keyword, "").trim().toLowerCase(),
        projectIds: uniqueStrings(source.projectIds),
        status: stringOrDefault(source.status, "").trim(),
        type: stringOrDefault(source.type, "").trim(),
        selectedProjectId: stringOrDefault(source.selectedProjectId || (source.selectedProject || {}).id, "").trim(),
        groupId: stringOrDefault(source.groupId, "").trim(),
        ownerOnly: source.ownerOnly === true,
        favoriteOnly: source.favoriteOnly === true,
        milestoneOnly: source.milestoneOnly === true,
        todayKey,
        weekStartKey,
        weekEndKey: weekStartKey ? addDaysToDateKey(weekStartKey, 6) : "",
        nextWeekStartKey,
        nextWeekEndKey: nextWeekStartKey ? addDaysToDateKey(nextWeekStartKey, 6) : ""
    };
}

export function buildScheduleTimelineData(input = {}) {
    const source = isPlainObject(input) ? input : {};
    const context = createSchedulePlannerFilterContext(source.context || source.filterContext || {});
    const projects = normalizePlannerProjects(source.projects, context);
    const rows = normalizePlannerRows(source.rows);
    const maxProjects = Math.max(1, Math.floor(numberOrDefault(source.maxProjects, SCHEDULE_PLANNER_TIMELINE_MAX_PROJECTS)));
    const projectEntries = {};
    const axisDates = [];

    projects.forEach(project => projectEntries[project.id] = []);
    rows.forEach(row => {
        if (!row || !isDateKey(row.date)) return;
        axisDates.push(row.date);
        projects.forEach(project => {
            const entry = createPlannerEntry(row, project);
            if (!entry || !plannerEntryMatchesContext(entry, context)) return;
            projectEntries[project.id].push(entry);
            axisDates.push(entry.date);
        });
    });

    const milestones = collectPlannerMilestones(source.milestones, projects, rows, context);
    milestones.forEach(milestone => axisDates.push(milestone.date));

    const axisStart = isDateKey(source.axisStartDate) ? source.axisStartDate : minDateKey(axisDates);
    const axisEnd = isDateKey(source.axisEndDate) ? source.axisEndDate : maxDateKey(axisDates);
    const axis = createPlannerDateAxis(axisStart, axisEnd);
    const commonMilestones = milestones
        .filter(milestone => milestone.scope === "common")
        .map(milestone => ({
            ...milestone,
            offsetPercent: getDateOffsetPercent(milestone.date, axis)
        }));

    const fullItems = projects
        .map((project, index) => {
            const entries = projectEntries[project.id] || [];
            const projectMilestones = milestones
                .filter(milestone => milestone.scope === "project" && milestone.projectId === project.id)
                .map(milestone => ({
                    ...milestone,
                    offsetPercent: getDateOffsetPercent(milestone.date, axis)
                }));
            const dependencyMarkers = entries
                .filter(entry => entry.dependencyCount > 0)
                .map(entry => ({
                    id: `${project.id}-${entry.date}-dependency`,
                    date: entry.date,
                    title: entry.dependencyLabel,
                    count: entry.dependencyCount,
                    offsetPercent: getDateOffsetPercent(entry.date, axis)
                }));

            if (entries.length === 0 && projectMilestones.length === 0 && dependencyMarkers.length === 0) return null;

            const entryDates = entries.map(entry => entry.date);
            const startDate = minDateKey(entryDates);
            const endDate = maxDateKey(entryDates);
            const hasSpan = !!(startDate && endDate);
            const startPercent = hasSpan ? getDateOffsetPercent(startDate, axis) : 0;
            const endPercent = hasSpan ? getDateOffsetPercent(endDate, axis) : startPercent;
            const widthPercent = hasSpan ? Math.max(axis.dayCount <= 1 ? 100 : 2, endPercent - startPercent) : 0;

            return {
                id: project.id,
                project,
                label: project.name || project.id,
                owner: stringOrDefault(project.owner, ""),
                pm: stringOrDefault(project.pm, ""),
                color: stringOrDefault(project.color, ""),
                order: index,
                startDate,
                endDate,
                startPercent,
                widthPercent,
                entryCount: entries.length,
                delayedCount: entries.filter(entry => isPlannerEntryDelayed(entry, context)).length,
                completedCount: entries.filter(entry => entry.status === "완료").length,
                dependencyCount: dependencyMarkers.reduce((total, marker) => total + marker.count, 0),
                milestones: projectMilestones,
                dependencyMarkers
            };
        })
        .filter(item => !!item);

    return {
        axisStart,
        axisEnd,
        axisLabels: createPlannerAxisLabels(axisStart, axisEnd),
        dayCount: axis.dayCount,
        items: fullItems.slice(0, maxProjects),
        totalItems: fullItems.length,
        truncatedCount: Math.max(0, fullItems.length - maxProjects),
        entryCount: fullItems.reduce((total, item) => total + item.entryCount, 0),
        milestoneCount: milestones.length,
        dependencyCount: fullItems.reduce((total, item) => total + item.dependencyCount, 0),
        commonMilestones,
        filterContext: context
    };
}

export function buildScheduleWorkloadSummary(input = {}) {
    const source = isPlainObject(input) ? input : {};
    const context = createSchedulePlannerFilterContext(source.context || source.filterContext || {});
    const projects = normalizePlannerProjects(source.projects, context);
    const rows = normalizePlannerRows(source.rows);
    const maxPeople = Math.max(1, Math.floor(numberOrDefault(source.maxPeople, SCHEDULE_PLANNER_WORKLOAD_MAX_PEOPLE)));
    const people = {};

    rows.forEach(row => {
        if (!row || !isDateKey(row.date)) return;
        projects.forEach(project => {
            const entry = createPlannerEntry(row, project);
            if (!entry || !plannerEntryMatchesContext(entry, context)) return;
            const assignments = collectPlannerAssignments(project);
            assignments.forEach(assignment => {
                const key = assignment.name;
                if (!people[key]) {
                    people[key] = {
                        id: `person-${key}`,
                        name: assignment.name,
                        roles: [],
                        projectIds: {},
                        totalCount: 0,
                        thisWeekCount: 0,
                        nextWeekCount: 0,
                        delayedCount: 0
                    };
                }
                if (people[key].roles.indexOf(assignment.role) < 0) people[key].roles.push(assignment.role);
                people[key].projectIds[project.id] = true;
                people[key].totalCount += 1;
                if (isDateWithinRange(row.date, context.weekStartKey, context.weekEndKey)) people[key].thisWeekCount += 1;
                if (isDateWithinRange(row.date, context.nextWeekStartKey, context.nextWeekEndKey)) people[key].nextWeekCount += 1;
                if (isPlannerEntryDelayed(entry, context)) people[key].delayedCount += 1;
            });
        });
    });

    const fullItems = Object.keys(people)
        .map(key => {
            const item = people[key];
            return {
                id: item.id,
                name: item.name,
                roles: item.roles.sort(),
                roleLabel: item.roles.sort().join("/"),
                projectCount: Object.keys(item.projectIds).length,
                totalCount: item.totalCount,
                thisWeekCount: item.thisWeekCount,
                nextWeekCount: item.nextWeekCount,
                delayedCount: item.delayedCount
            };
        })
        .sort((a, b) => {
            const delayedOrder = b.delayedCount - a.delayedCount;
            if (delayedOrder !== 0) return delayedOrder;
            const weekOrder = (b.thisWeekCount + b.nextWeekCount) - (a.thisWeekCount + a.nextWeekCount);
            if (weekOrder !== 0) return weekOrder;
            return a.name.localeCompare(b.name);
        });

    return {
        weekStartKey: context.weekStartKey,
        weekEndKey: context.weekEndKey,
        nextWeekStartKey: context.nextWeekStartKey,
        nextWeekEndKey: context.nextWeekEndKey,
        items: fullItems.slice(0, maxPeople),
        totalItems: fullItems.length,
        truncatedCount: Math.max(0, fullItems.length - maxPeople),
        totalEntryCount: fullItems.reduce((total, item) => total + item.totalCount, 0),
        delayedEntryCount: fullItems.reduce((total, item) => total + item.delayedCount, 0),
        filterContext: context
    };
}

function normalizePlannerProjects(projects, context = {}) {
    if (!Array.isArray(projects)) return [];
    const allowedProjectIds = toSet(context.projectIds || []);
    const hasProjectScope = Object.keys(allowedProjectIds).length > 0;
    const seen = {};
    return projects
        .filter(project => isPlainObject(project) && !isHiddenProject(project))
        .filter(project => {
            const id = stringOrDefault(project.id, "").trim();
            if (!id || seen[id]) return false;
            if (hasProjectScope && allowedProjectIds[id] !== true) return false;
            seen[id] = true;
            return true;
        });
}

function normalizePlannerRows(rows) {
    if (!Array.isArray(rows)) return [];
    return rows
        .filter(row => isPlainObject(row) && isDateKey(row.date))
        .slice()
        .sort((a, b) => stringOrDefault(a.date, "").localeCompare(stringOrDefault(b.date, "")));
}

function createPlannerEntry(row, project) {
    const projectId = project && project.id ? String(project.id) : "";
    if (!row || !projectId || !isDateKey(row.date)) return null;

    const text = getPlannerCellText(row, projectId);
    const meta = getPlannerCellMeta(row, projectId);
    const hasExplicitMeta = !isDefaultPlannerCellMeta(meta);
    if (!text && !hasExplicitMeta) return null;

    const dependencyCount = meta.dependencies.predecessors.length + meta.dependencies.successors.length;
    return {
        date: row.date,
        row,
        project,
        projectId,
        text,
        status: meta.status,
        type: meta.type,
        time: meta.time,
        dependencyCount,
        dependencyLabel: dependencyCount > 0 ? `의존성 ${dependencyCount}개` : "",
        searchText: [
            row.date,
            row.dateLabel,
            row.note,
            project.name,
            project.pm,
            project.owner,
            text,
            meta.status,
            meta.type,
            dependencyCount > 0 ? "의존성" : ""
        ].filter(part => stringOrDefault(part, "").trim().length > 0).join(" ").toLowerCase()
    };
}

function plannerEntryMatchesContext(entry, context = {}) {
    if (!entry) return false;
    if (context.status && entry.status !== context.status) return false;
    if (context.type && entry.type !== context.type) return false;
    if (context.keyword && entry.searchText.indexOf(context.keyword) < 0) return false;
    return true;
}

function collectPlannerMilestones(milestones, projects, rows, context = {}) {
    if (!Array.isArray(milestones)) return [];
    const projectMap = mapProjectsById(projects);
    const rowDateSet = toSet(collectDateKeys(rows));
    const hasRowScope = Object.keys(rowDateSet).length > 0;
    const keyword = stringOrDefault(context.keyword, "").trim().toLowerCase();
    const blocksEntryMilestones = (context.status || context.type) && context.milestoneOnly !== true;

    return milestones
        .filter(milestone => isPlainObject(milestone) && isDateKey(milestone.date))
        .filter(milestone => !hasRowScope || rowDateSet[milestone.date] === true)
        .filter(milestone => {
            if (blocksEntryMilestones) return false;
            if (milestone.scope === "project") return !!projectMap[milestone.projectId];
            return milestone.scope === "common" || !milestone.scope;
        })
        .filter(milestone => {
            if (!keyword) return true;
            const project = milestone.scope === "project" ? projectMap[milestone.projectId] : null;
            const text = [
                milestone.title,
                milestone.note,
                milestone.date,
                milestone.scope === "project" ? (project && project.name) : "공통",
                "마일스톤"
            ].filter(part => stringOrDefault(part, "").trim().length > 0).join(" ").toLowerCase();
            return text.indexOf(keyword) >= 0;
        })
        .map((milestone, index) => ({
            id: stringOrDefault(milestone.id, `milestone-${index + 1}`),
            title: stringOrDefault(milestone.title || milestone.name, "마일스톤"),
            date: milestone.date,
            scope: milestone.scope === "project" ? "project" : "common",
            projectId: milestone.scope === "project" ? stringOrDefault(milestone.projectId, "") : "",
            color: normalizeMilestoneColor(milestone.color),
            note: stringOrDefault(milestone.note, "")
        }));
}

function collectPlannerAssignments(project) {
    const owner = stringOrDefault(project && project.owner, "").trim();
    const pm = stringOrDefault(project && project.pm, "").trim();
    if (!owner && !pm) return [{ name: "미지정", role: "미지정" }];
    if (owner && pm && owner === pm) return [{ name: owner, role: "PM/담당" }];

    const assignments = [];
    if (owner) assignments.push({ name: owner, role: "담당" });
    if (pm) assignments.push({ name: pm, role: "PM" });
    return assignments;
}

function getPlannerCellText(row, projectId) {
    const cells = isPlainObject(row && row.cells) ? row.cells : {};
    const value = cells[projectId];
    if (isPlainObject(value)) {
        if (value.memo !== undefined && value.memo !== null) return stringOrDefault(value.memo, "").trim();
        if (value.value !== undefined && value.value !== null) return stringOrDefault(value.value, "").trim();
        if (value.body !== undefined && value.body !== null) return stringOrDefault(value.body, "").trim();
        return "";
    }
    return stringOrDefault(value, "").trim();
}

function getPlannerCellMeta(row, projectId) {
    const cells = isPlainObject(row && row.cells) ? row.cells : {};
    const metaMap = isPlainObject(row && row.cellMeta) ? row.cellMeta : {};
    const cell = isPlainObject(cells[projectId]) ? cells[projectId] : {};
    const meta = isPlainObject(metaMap[projectId]) ? metaMap[projectId] : {};
    const status = stringOrDefault(meta.status !== undefined ? meta.status : (cell.taskStatus !== undefined ? cell.taskStatus : cell.status), "").trim() || "예정";
    const type = stringOrDefault(meta.type !== undefined ? meta.type : (cell.entryType !== undefined ? cell.entryType : (cell.type !== undefined ? cell.type : cell.entry_type)), "").trim() || "업무";
    const time = stringOrDefault(meta.time !== undefined ? meta.time : (cell.scheduleTime !== undefined ? cell.scheduleTime : (cell.time !== undefined ? cell.time : cell.schedule_time)), "").trim();
    const dependencySource = meta.dependencies !== undefined ? meta.dependencies : (
        cell.dependencies !== undefined ? cell.dependencies : (
            meta.predecessors !== undefined || meta.successors !== undefined ? meta : cell
        )
    );

    return {
        status,
        type,
        time,
        dependencies: normalizePlannerDependencies(dependencySource)
    };
}

function normalizePlannerDependencies(input) {
    const source = isPlainObject(input) ? input : {};
    return {
        predecessors: normalizePlannerDependencyList(source.predecessors !== undefined ? source.predecessors : source.predecessor),
        successors: normalizePlannerDependencyList(source.successors !== undefined ? source.successors : source.successor)
    };
}

function normalizePlannerDependencyList(input) {
    const list = Array.isArray(input) ? input : (input ? [input] : []);
    const seen = {};
    return list
        .map(item => {
            const source = isPlainObject(item) ? item : {};
            const date = stringOrDefault(source.date || source.targetDate || source.dateKey, "").trim();
            const projectId = stringOrDefault(source.projectId || source.project_id || source.targetProjectId || source.target_project_id, "").trim();
            if (!isDateKey(date) || !projectId) return null;
            return { date, projectId };
        })
        .filter(item => {
            if (!item) return false;
            const key = `${item.date}:${item.projectId}`;
            if (seen[key]) return false;
            seen[key] = true;
            return true;
        });
}

function isDefaultPlannerCellMeta(meta) {
    if (!meta) return true;
    const dependencies = normalizePlannerDependencies(meta.dependencies);
    return (meta.status || "예정") === "예정"
        && (meta.type || "업무") === "업무"
        && !meta.time
        && dependencies.predecessors.length === 0
        && dependencies.successors.length === 0;
}

function isPlannerEntryDelayed(entry, context = {}) {
    if (!entry) return false;
    if (entry.status === "지연") return true;
    const todayKey = stringOrDefault(context.todayKey, "");
    return isDateKey(todayKey) && entry.date < todayKey && entry.status !== "완료";
}

function createPlannerDateAxis(axisStart, axisEnd) {
    const start = isDateKey(axisStart) ? axisStart : "";
    const end = isDateKey(axisEnd) ? axisEnd : start;
    const dayCount = start && end ? Math.max(1, daysBetweenDateKeys(start, end) + 1) : 1;
    return { start, end, dayCount };
}

function createPlannerAxisLabels(axisStart, axisEnd) {
    if (!isDateKey(axisStart) || !isDateKey(axisEnd)) return [];
    if (axisStart === axisEnd) return [{ date: axisStart, label: formatPlannerShortDate(axisStart), offsetPercent: 0 }];
    const middle = addDaysToDateKey(axisStart, Math.floor(daysBetweenDateKeys(axisStart, axisEnd) / 2));
    return [
        { date: axisStart, label: formatPlannerShortDate(axisStart), offsetPercent: 0 },
        { date: middle, label: formatPlannerShortDate(middle), offsetPercent: 50 },
        { date: axisEnd, label: formatPlannerShortDate(axisEnd), offsetPercent: 100 }
    ];
}

function getDateOffsetPercent(dateKey, axis) {
    if (!isDateKey(dateKey) || !axis || !isDateKey(axis.start) || !isDateKey(axis.end)) return 0;
    if (axis.dayCount <= 1) return 0;
    const offset = daysBetweenDateKeys(axis.start, dateKey);
    return Math.max(0, Math.min(100, Math.round((offset / Math.max(1, axis.dayCount - 1)) * 1000) / 10));
}

function minDateKey(values) {
    const dates = uniqueStrings(values).filter(dateKey => isDateKey(dateKey)).sort();
    return dates.length > 0 ? dates[0] : "";
}

function maxDateKey(values) {
    const dates = uniqueStrings(values).filter(dateKey => isDateKey(dateKey)).sort();
    return dates.length > 0 ? dates[dates.length - 1] : "";
}

function parseDateKey(dateKey) {
    if (!isDateKey(dateKey)) return null;
    const parts = dateKey.split("-").map(part => parseInt(part, 10));
    return new Date(parts[0], parts[1] - 1, parts[2]);
}

function formatDateKey(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
    return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0")
    ].join("-");
}

function addDaysToDateKey(dateKey, days) {
    const date = parseDateKey(dateKey);
    if (!date) return "";
    date.setDate(date.getDate() + Math.round(numberOrDefault(days, 0)));
    return formatDateKey(date);
}

function daysBetweenDateKeys(startKey, endKey) {
    const start = parseDateKey(startKey);
    const end = parseDateKey(endKey);
    if (!start || !end) return 0;
    const dayMs = 24 * 60 * 60 * 1000;
    return Math.round((end.getTime() - start.getTime()) / dayMs);
}

function getPlannerWeekStartKey(dateKey) {
    const date = parseDateKey(dateKey);
    if (!date) return "";
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    date.setDate(date.getDate() + diff);
    return formatDateKey(date);
}

function isDateWithinRange(dateKey, startKey, endKey) {
    if (!isDateKey(dateKey) || !isDateKey(startKey) || !isDateKey(endKey)) return false;
    return dateKey >= startKey && dateKey <= endKey;
}

function formatPlannerShortDate(dateKey) {
    if (!isDateKey(dateKey)) return "";
    const parts = dateKey.split("-");
    return `${Number(parts[1])}.${Number(parts[2])}`;
}

function collectProjectIds(projects) {
    if (!Array.isArray(projects)) return [];
    return projects
        .map(project => isPlainObject(project) ? stringOrDefault(project.id, "").trim() : "")
        .filter(id => id.length > 0)
        .filter((id, index, ids) => ids.indexOf(id) === index);
}

function collectDateKeys(rows) {
    if (!Array.isArray(rows)) return [];
    const seen = {};
    return rows
        .map(row => isPlainObject(row) ? stringOrDefault(row.date, "").trim() : "")
        .filter(dateKey => {
            if (!isDateKey(dateKey) || seen[dateKey]) return false;
            seen[dateKey] = true;
            return true;
        });
}

function collectVisibleProjectIds(projects) {
    if (!Array.isArray(projects)) return [];
    const seen = {};
    return projects
        .map(project => {
            if (!isPlainObject(project) || isHiddenProject(project)) return "";
            return stringOrDefault(project.id, "").trim();
        })
        .filter(id => {
            if (!id || seen[id]) return false;
            seen[id] = true;
            return true;
        });
}

function collectUngroupedProjectIds(projects, projectGroups) {
    const projectIds = collectProjectIds(projects);
    const projectIdSet = toSet(projectIds);
    const assigned = {};
    if (Array.isArray(projectGroups)) {
        projectGroups.forEach(group => {
            if (!isPlainObject(group)) return;
            uniqueStrings(group.projectIds).forEach(projectId => {
                if (projectIdSet[projectId]) assigned[projectId] = true;
            });
        });
    }
    return projectIds.filter(projectId => !assigned[projectId]);
}

function collectCollapsedProjectGroupIds(projectGroups) {
    if (!Array.isArray(projectGroups)) return [];
    return uniqueStrings(projectGroups
        .filter(group => isPlainObject(group) && group.collapsed === true)
        .map(group => group.id));
}

function normalizeCollapsedGroupIds(collapsedGroupIds, projectGroups, projects) {
    const projectIds = collectProjectIds(projects);
    const validGroupIds = {};
    if (Array.isArray(projectGroups)) {
        projectGroups.forEach(group => {
            if (!isPlainObject(group) || !group.id || !Array.isArray(group.projectIds) || group.projectIds.length === 0) return;
            validGroupIds[group.id] = true;
        });
    }

    if (collectUngroupedProjectIds(projects, projectGroups).length > 0 && projectIds.length > 0) {
        validGroupIds[UNGROUPED_PROJECT_GROUP_ID] = true;
    }

    return uniqueStrings(collapsedGroupIds).filter(groupId => validGroupIds[groupId] === true);
}

function mapProjectsById(projects) {
    const map = {};
    if (!Array.isArray(projects)) return map;
    projects.forEach(project => {
        if (!isPlainObject(project)) return;
        const id = stringOrDefault(project.id, "").trim();
        if (id && !map[id]) map[id] = project;
    });
    return map;
}

function isFavoriteProject(project) {
    return isPlainObject(project) && (project.isFavorite === true || project.favorite === true);
}

function isHiddenProject(project) {
    return isPlainObject(project) && project.hidden === true;
}

function compareProjectGroupCandidates(a, b) {
    const order = a.sourceOrder - b.sourceOrder;
    if (order !== 0) return order;
    return a.sourceIndex - b.sourceIndex;
}

function getMoveTargetIndex(index, length, direction) {
    if (index < 0 || length <= 1) return index;
    const move = direction === "up" ? -1 : (direction === "down" ? 1 : 0);
    if (move === 0) return index;
    return Math.min(length - 1, Math.max(0, index + move));
}

function swapArrayItems(items, fromIndex, toIndex) {
    if (!Array.isArray(items)) return [];
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length || fromIndex === toIndex) {
        return items.slice();
    }
    const nextItems = items.slice();
    const item = nextItems[fromIndex];
    nextItems[fromIndex] = nextItems[toIndex];
    nextItems[toIndex] = item;
    return nextItems;
}

function moveArrayItemToIndex(items, fromIndex, toIndex) {
    if (!Array.isArray(items)) return [];
    if (fromIndex < 0 || fromIndex >= items.length || items.length <= 1) return items.slice();
    const nextItems = items.slice();
    const item = nextItems.splice(fromIndex, 1)[0];
    const numericTargetIndex = Number(toIndex);
    if (!Number.isFinite(numericTargetIndex)) {
        nextItems.splice(fromIndex, 0, item);
        return nextItems;
    }
    const normalizedTargetIndex = Math.min(nextItems.length, Math.max(0, Math.floor(numericTargetIndex)));
    nextItems.splice(normalizedTargetIndex, 0, item);
    return nextItems;
}

function arraysEqual(left, right) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
    return left.every((item, index) => item === right[index]);
}

function normalizeProjectSortOrders(projects) {
    if (!Array.isArray(projects)) return [];
    return projects.map((project, index) => {
        if (!isPlainObject(project)) return project;
        return {
            ...project,
            sortOrder: index
        };
    });
}

function normalizeProjectGroupOrder(group, fallback) {
    const source = group.order === undefined ? group.sortOrder : group.order;
    const order = Number(source);
    return Number.isFinite(order) ? order : fallback;
}

function normalizeProjectGroupColor(value) {
    const color = stringOrDefault(value, "").trim();
    return /^#[0-9A-Fa-f]{6}$/.test(color) ? color : DEFAULT_PROJECT_GROUP_COLOR;
}

function normalizeMilestoneColor(value) {
    const color = stringOrDefault(value, "").trim();
    return /^#[0-9A-Fa-f]{6}$/.test(color) ? color : DEFAULT_MILESTONE_COLOR;
}

function compareMilestones(a, b) {
    const dateOrder = a.date.localeCompare(b.date);
    if (dateOrder !== 0) return dateOrder;
    const scopeOrder = a.scope === b.scope ? 0 : (a.scope === "common" ? -1 : 1);
    if (scopeOrder !== 0) return scopeOrder;
    const order = a.order - b.order;
    if (order !== 0) return order;
    return a.title.localeCompare(b.title);
}

function boolOrDefault(value, fallback) {
    if (value === true || value === false) return value;
    return fallback;
}

function stringOrDefault(value, fallback) {
    if (value === undefined || value === null) return fallback;
    return String(value);
}

function numberOrDefault(value, fallback) {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : fallback;
}

function pickStringKey(value, options, fallback) {
    const key = stringOrDefault(value, "");
    return Object.prototype.hasOwnProperty.call(options, key) ? key : fallback;
}

function pickStringValue(value, options, fallback) {
    const text = stringOrDefault(value, "");
    return options.indexOf(text) >= 0 ? text : fallback;
}

function uniqueStrings(input) {
    if (!Array.isArray(input)) return [];
    const seen = {};
    return input
        .map(value => stringOrDefault(value, "").trim())
        .filter(value => {
            if (!value || seen[value]) return false;
            seen[value] = true;
            return true;
        });
}

function toSet(values) {
    const result = {};
    if (!Array.isArray(values)) return result;
    values.forEach(value => {
        const key = stringOrDefault(value, "").trim();
        if (key) result[key] = true;
    });
    return result;
}

function isPlainObject(value) {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

function isDateKey(value) {
    return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
}
